import { useState } from "react";
import { MessageSquare, RotateCcw, Eye, AlertTriangle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { PageHeader } from "@/components/layout/page-header";
import { PageTabs } from "@/components/layout/page-tabs";
import { PageToolbar, PageSearch } from "@/components/layout/page-toolbar";
import { StatCard, StatCardGrid } from "@/components/data/stat-card";
import { StatusBadge } from "@/components/data/status-badge";
import { EmptyState, NadaEncontrado } from "@/components/data/empty-state";
import { estadoDaConsulta } from "@/components/data/estado-consulta";
import {
  DataTable, DataTableHeader, DataTableBody, DataTableRow, DataTableHead, DataTableCell,
} from "@/components/data/data-table";
import { AdminLista, AdminListaItem, AdminTabela } from "@/components/admin/admin-lista";
import { ChipsDeSituacao } from "@/components/mercado/painel";
import { BolhaWhatsApp, ConversaWhatsApp, SeloEntrega, SeloPublico, SeloStatusEnvio } from "@/components/avisos/avisos-ui";
import { useEstadoNaUrl } from "@/lib/estado-na-url";
import { useNavegacao } from "@/lib/navegacao";
import {
  useEnviosAviso, useModelosAviso, useRegrasAviso, useSalvarRegrasAviso, useTentarDeNovo, useTextoDoEnvio,
} from "@/hooks/use-avisos";
import { FILTROS_ENVIO, type CanalAvisoDTO, type FiltroEnvio, type ModeloResumoDTO } from "@shared/avisos-admin";
import { FAIXA_ENVIO_AVISO, JANELAS_AVISO_MIN, LIMITES_AVISO_DIA, LIMITES_SILENCIO_AVISO, erroNoSilencio } from "@shared/avisos";

// Plataforma › Mensagens WhatsApp — os avisos automáticos do número do Rizoma.
// Três abas na URL (`?aba=`): Modelos, Envios, Regras e canal. O editor de um
// modelo é outra rota (/plataforma/mensagens/:chave).

// ⚠️ Constantes de MÓDULO (deps de useMemo dentro do hook) — ver estado-na-url.
const PADRAO = { aba: "modelos", publico: "todos", status: "todos", q: "" };
const PERMITIDOS = {
  aba: ["modelos", "envios", "regras"],
  publico: ["todos", "PRODUTOR", "COMPRADOR", "pausados"],
  status: FILTROS_ENVIO as unknown as string[],
} as const;

const ABAS = [
  { id: "modelos", label: "Modelos" },
  { id: "envios", label: "Envios" },
  { id: "regras", label: "Regras e canal" },
];

export function ChipCanal({ canal }: { canal: CanalAvisoDTO | undefined }) {
  if (!canal) return null;
  return <StatusBadge tom={canal.real ? "sucesso" : "atencao"} ponto testId="chip-canal">Canal: {canal.rotulo}</StatusBadge>;
}

function fmtData(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
}

export function rotuloVersao(m: Pick<ModeloResumoDTO, "versao">): string {
  if (!m.versao) return "padrão";
  const por = m.versao.por ? ` · ${m.versao.por.split(" ")[0]}` : "";
  return `v${m.versao.numero} · ${fmtData(m.versao.em)}${por}`;
}

export default function PlataformaMensagens() {
  const { estado, definir } = useEstadoNaUrl(PADRAO, PERMITIDOS);
  const modelos = useModelosAviso();
  return (
    <div className="space-y-5">
      <PageHeader
        titulo="Mensagens WhatsApp"
        descricao="Avisos automáticos que saem pelo número do Rizoma."
        acao={<ChipCanal canal={modelos.data?.canal} />}
      />
      <PageTabs abas={ABAS} ativa={estado.aba} onChange={aba => definir({ aba })} />
      {estado.aba === "modelos" && <AbaModelos publico={estado.publico} onPublico={p => definir({ publico: p })} irParaFalhas={() => definir({ aba: "envios", status: "FALHOU" })} />}
      {estado.aba === "envios" && <AbaEnvios status={estado.status as FiltroEnvio} q={estado.q} definir={definir} />}
      {estado.aba === "regras" && <AbaRegras />}
    </div>
  );
}

// ─── Modelos ─────────────────────────────────────────────────────────────────

function AbaModelos({ publico, onPublico, irParaFalhas }: { publico: string; onPublico: (p: string) => void; irParaFalhas: () => void }) {
  const consulta = useModelosAviso();
  const regras = useRegrasAviso();
  const { abrir } = useNavegacao();
  const aguardando = estadoDaConsulta(consulta, { variante: "tabela" });
  if (aguardando) return <>{aguardando}</>;
  const { semana, modelos } = consulta.data!;
  const janela = regras.data?.janelaMin;

  const lista = modelos.filter(m =>
    publico === "todos" ? true
      : publico === "pausados" ? m.pausado
        : m.publico === publico || m.publico === "AMBOS");
  const n = (f: (m: ModeloResumoDTO) => boolean) => modelos.filter(f).length;

  return (
    <div className="space-y-4">
      <StatCardGrid>
        <StatCard rotulo="Enviadas · 7 dias" valor={String(semana.enviadas)} legenda={`para ${semana.pessoas} ${semana.pessoas === 1 ? "pessoa" : "pessoas"}`} testId="tile-enviadas" />
        <StatCard
          rotulo="Falhas · 7 dias"
          valor={String(semana.falhas)}
          testId="tile-falhas"
          legenda={
            <span className="space-y-0.5">
              <span className="block">{semana.ultimaFalha ?? "nenhuma"}</span>
              {semana.falhas > 0 && (
                <button type="button" className="text-primary font-semibold" onClick={irParaFalhas} data-testid="link-ver-falhas">Ver envios →</button>
              )}
            </span>
          }
        />
        <StatCard rotulo="Agrupadas" valor={String(semana.agrupadasEventos)} legenda={`eventos que viraram ${semana.agrupadasMensagens} ${semana.agrupadasMensagens === 1 ? "mensagem" : "mensagens"}`} testId="tile-agrupadas" />
        <StatCard rotulo="Seguradas pelo horário" valor={String(semana.seguradas)} legenda="esperaram o fim do horário silencioso" testId="tile-seguradas" />
      </StatCardGrid>

      <ChipsDeSituacao
        testId="chips-publico"
        valor={publico}
        onChange={onPublico}
        opcoes={[
          { id: "todos", label: "Todos", n: modelos.length },
          { id: "PRODUTOR", label: "Produtor", n: n(m => m.publico !== "COMPRADOR") },
          { id: "COMPRADOR", label: "Comprador", n: n(m => m.publico !== "PRODUTOR") },
          { id: "pausados", label: "Pausados", n: n(m => m.pausado) },
        ]}
      />

      {lista.length === 0 ? (
        <EmptyState icone={MessageSquare} titulo="Nenhum modelo" descricao={modelos.length === 0 ? "Nenhum aviso foi definido no código ainda." : "Nenhum modelo com este filtro."} testId="empty-modelos" />
      ) : (
        <>
          <AdminLista>
            {lista.map(m => (
              <AdminListaItem
                key={m.chave}
                testId={`card-modelo-${m.chave}`}
                onClick={() => abrir(`/plataforma/mensagens/${m.chave}`)}
                identidade={
                  <span className="block space-y-1">
                    <span className="flex flex-wrap gap-1"><SeloPublico publico={m.publico} /><SeloEntrega entrega={m.entrega} janelaMin={janela} />{m.pausado && <StatusBadge tom="neutro">Pausado</StatusBadge>}</span>
                    <span className="block font-semibold">{m.nome}</span>
                  </span>
                }
                meta={
                  <span className="block space-y-0.5">
                    <span className="block text-xs">{m.gatilho}</span>
                    <span className="block text-xs text-muted-foreground">
                      {m.enviadas30d} enviadas em 30 d{m.falhas30d > 0 && <span className="text-destructive"> · {m.falhas30d} {m.falhas30d === 1 ? "falha" : "falhas"}</span>} · {rotuloVersao(m)}
                    </span>
                  </span>
                }
              />
            ))}
          </AdminLista>

          <AdminTabela>
            <DataTable colunas={[null, "7.5rem", "14rem", "8.5rem", "5.5rem", "4.5rem", "6rem", "8rem"]}>
              <DataTableHeader>
                <DataTableRow>
                  <DataTableHead>Modelo</DataTableHead>
                  <DataTableHead>Para</DataTableHead>
                  <DataTableHead>Quando sai</DataTableHead>
                  <DataTableHead>Entrega</DataTableHead>
                  <DataTableHead num>Enviadas 30 d</DataTableHead>
                  <DataTableHead num>Falhas</DataTableHead>
                  <DataTableHead>Situação</DataTableHead>
                  <DataTableHead>Versão</DataTableHead>
                </DataTableRow>
              </DataTableHeader>
              <DataTableBody>
                {lista.map(m => (
                  <DataTableRow key={m.chave} className="cursor-pointer" onClick={() => abrir(`/plataforma/mensagens/${m.chave}`)} data-testid={`linha-modelo-${m.chave}`}>
                    <DataTableCell destaque className="whitespace-normal">
                      <p className="line-clamp-2 leading-snug">{m.nome}</p>
                      <p className="text-xs text-muted-foreground font-mono font-normal truncate">{m.chave}</p>
                    </DataTableCell>
                    <DataTableCell><SeloPublico publico={m.publico} /></DataTableCell>
                    <DataTableCell className="text-xs whitespace-normal">{m.gatilho}</DataTableCell>
                    <DataTableCell><SeloEntrega entrega={m.entrega} janelaMin={janela} /></DataTableCell>
                    <DataTableCell className="text-right tabular-nums font-mono">{m.enviadas30d}</DataTableCell>
                    <DataTableCell className={"text-right tabular-nums font-mono " + (m.falhas30d > 0 ? "text-destructive" : "")}>{m.falhas30d}</DataTableCell>
                    <DataTableCell>{m.pausado ? <StatusBadge tom="neutro" ponto>Pausado</StatusBadge> : <StatusBadge tom="sucesso" ponto>Ativo</StatusBadge>}</DataTableCell>
                    <DataTableCell className="text-xs text-muted-foreground">{rotuloVersao(m)}</DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </AdminTabela>
        </>
      )}
    </div>
  );
}

// ─── Envios ──────────────────────────────────────────────────────────────────

const ROTULO_FILTRO: Record<FiltroEnvio, string> = {
  todos: "Todos", ENVIADA: "Enviada", SEGURADA: "Segurada", SUPRIMIDA: "Suprimida", CANCELADA: "Cancelada", FALHOU: "Falhou", PENDENTE: "Na fila",
};

function AbaEnvios({ status, q, definir }: { status: FiltroEnvio; q: string; definir: (p: Partial<typeof PADRAO>) => void }) {
  const consulta = useEnviosAviso(status, q);
  const tentar = useTentarDeNovo();
  const { toast } = useToast();
  const [vendo, setVendo] = useState<string | null>(null);
  const aguardando = estadoDaConsulta(consulta, { variante: "tabela" });
  const dados = consulta.data;

  function tentarDeNovo(id: string) {
    tentar.mutate(id, {
      onSuccess: () => toast({ title: "Voltou para a fila", description: "Sai no próximo tique do envio (até 2 min)." }),
      onError: (e) => toast({ title: "Não deu para tentar de novo", description: e.message, variant: "destructive" }),
    });
  }

  // No cartão (celular) o "Ver texto" é só o ícone: com rótulo ele espremia a
  // chave do modelo, que é o que identifica a linha.
  const acoes = (id: string, podeTentar: boolean, compacto = false) => (podeTentar
    ? <Button size="sm" variant="outline" onClick={e => { e.stopPropagation(); tentarDeNovo(id); }} disabled={tentar.isPending} data-testid={`button-tentar-${id}`}><RotateCcw className="h-3.5 w-3.5 mr-1.5" />Tentar de novo</Button>
    : compacto
      ? <Button size="icon" variant="ghost" aria-label="Ver texto" onClick={e => { e.stopPropagation(); setVendo(id); }} data-testid={`button-ver-texto-${id}`}><Eye className="h-4 w-4" /></Button>
      : <Button size="sm" variant="ghost" onClick={e => { e.stopPropagation(); setVendo(id); }} data-testid={`button-ver-texto-${id}`}><Eye className="h-3.5 w-3.5 mr-1.5" />Ver texto</Button>);

  return (
    <div className="space-y-4">
      <PageToolbar fim={<PageSearch valor={q} onChange={v => definir({ q: v })} placeholder="Pessoa, telefone ou modelo" testId="input-busca-envios" />}>
        <ChipsDeSituacao
          testId="chips-status-envio"
          valor={status}
          onChange={v => definir({ status: v })}
          opcoes={FILTROS_ENVIO.filter(f => f !== "PENDENTE" || (dados?.contagens.PENDENTE ?? 0) > 0)
            .map(f => ({ id: f, label: ROTULO_FILTRO[f], n: dados ? dados.contagens[f] : 0 }))}
        />
      </PageToolbar>

      {aguardando ?? (
        dados!.linhas.length === 0 ? (
          q.trim() ? <NadaEncontrado termo={q.trim()} /> : <EmptyState icone={Send} titulo="Nenhum envio" descricao="Nenhum aviso nesta situação." testId="empty-envios" />
        ) : (
          <>
            <AdminLista>
              {dados!.linhas.map(e => (
                <AdminListaItem
                  key={e.id}
                  testId={`card-envio-${e.id}`}
                  identidade={
                    <span className="block space-y-1">
                      <span className="flex items-center justify-between gap-2"><span className="font-semibold truncate">{e.pessoa.nome}</span><SeloStatusEnvio status={e.status} /></span>
                      <span className="block font-mono text-[11.5px] text-muted-foreground">{e.quandoTexto} · {e.chave}</span>
                    </span>
                  }
                  meta={e.detalhe ? <span className="text-xs">{e.detalhe}</span> : undefined}
                  acoes={acoes(e.id, e.podeTentarDeNovo, true)}
                />
              ))}
            </AdminLista>
            <AdminTabela>
              <DataTable colunas={["8rem", "12rem", null, "7.5rem", null, "9rem"]}>
                <DataTableHeader>
                  <DataTableRow>
                    <DataTableHead>Quando</DataTableHead>
                    <DataTableHead>Modelo</DataTableHead>
                    <DataTableHead>Para</DataTableHead>
                    <DataTableHead>Situação</DataTableHead>
                    <DataTableHead>Detalhe</DataTableHead>
                    <DataTableHead />
                  </DataTableRow>
                </DataTableHeader>
                <DataTableBody>
                  {dados!.linhas.map(e => (
                    <DataTableRow key={e.id} data-testid={`linha-envio-${e.id}`}>
                      <DataTableCell className="font-mono text-xs tabular-nums">{e.quandoTexto}</DataTableCell>
                      <DataTableCell className="font-mono text-xs truncate" title={e.modeloNome}>{e.chave}</DataTableCell>
                      <DataTableCell>
                        <p className="font-semibold truncate">{e.pessoa.nome}<span className="font-normal text-muted-foreground"> · {e.pessoa.org}</span></p>
                        <p className="text-xs text-muted-foreground font-mono">{e.pessoa.telefone}</p>
                      </DataTableCell>
                      <DataTableCell><SeloStatusEnvio status={e.status} /></DataTableCell>
                      <DataTableCell className="text-xs whitespace-normal">{e.detalhe}</DataTableCell>
                      <DataTableCell className="text-right">{acoes(e.id, e.podeTentarDeNovo)}</DataTableCell>
                    </DataTableRow>
                  ))}
                </DataTableBody>
              </DataTable>
            </AdminTabela>
          </>
        )
      )}
      {vendo && <DialogTextoEnvio id={vendo} onFechar={() => setVendo(null)} />}
    </div>
  );
}

function DialogTextoEnvio({ id, onFechar }: { id: string; onFechar: () => void }) {
  const consulta = useTextoDoEnvio(id);
  return (
    <Dialog open onOpenChange={a => { if (!a) onFechar(); }}>
      <DialogContent className="max-w-md" data-testid="dialog-texto-envio">
        <DialogHeader>
          <DialogTitle>Texto do aviso</DialogTitle>
          <DialogDescription>
            {consulta.data?.enviado === false ? "Não saiu. Este é o texto que sairia hoje, com o modelo no ar." : "O texto exato que saiu do número do Rizoma."}
          </DialogDescription>
        </DialogHeader>
        {estadoDaConsulta(consulta, { variante: "painel" }) ?? (
          <ConversaWhatsApp><BolhaWhatsApp texto={consulta.data!.texto} /></ConversaWhatsApp>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ─── Regras e canal ──────────────────────────────────────────────────────────

function LinhaRegra({ titulo, descricao, children, testId }: { titulo: string; descricao: string; children: React.ReactNode; testId?: string }) {
  return (
    <div className="grid gap-3 py-3.5 border-t first:border-t-0 md:grid-cols-[minmax(0,1fr)_280px] md:items-center md:gap-6" data-testid={testId}>
      <div>
        <p className="text-sm font-semibold">{titulo}</p>
        <p className="text-xs text-muted-foreground">{descricao}</p>
      </div>
      <div className="flex items-center gap-2 md:justify-end">{children}</div>
    </div>
  );
}

function AbaRegras() {
  const consulta = useRegrasAviso();
  const salvar = useSalvarRegrasAviso();
  const { toast } = useToast();
  const [inicio, setInicio] = useState<string | null>(null);
  const [fim, setFim] = useState<string | null>(null);
  const aguardando = estadoDaConsulta(consulta, { variante: "painel" });
  if (aguardando) return <>{aguardando}</>;
  const r = consulta.data!;

  // O servidor valida a faixa também (400); aqui a frase aparece antes de ir.
  function salvarSilencio(campo: "inicio" | "fim", valor: string | null, atual: string, limpar: () => void) {
    if (valor && valor !== atual) {
      const erro = erroNoSilencio(campo, valor);
      if (erro) toast({ title: "Horário fora da faixa", description: erro, variant: "destructive" });
      else gravar(campo === "inicio" ? { silencioInicio: valor } : { silencioFim: valor }, "Horário silencioso salvo");
    }
    limpar();
  }

  function gravar(patch: Parameters<typeof salvar.mutate>[0], ok: string) {
    salvar.mutate(patch, {
      onSuccess: () => toast({ title: ok }),
      onError: (e) => toast({ title: "Não deu para salvar", description: e.message, variant: "destructive" }),
    });
  }

  return (
    <div className="space-y-4 max-w-3xl">
      <div className="flex gap-2.5 rounded-lg border border-atencao/30 bg-atencao-suave p-3 text-sm" data-testid="banner-numero-compartilhado">
        <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0 text-atencao" />
        <p>
          <b>Número compartilhado com o AgroPlat.</b> As respostas vão para o agente do AgroPlat, por isso todo aviso termina em
          “aviso automático” e leva um link, nunca uma pergunta. Antes de abrir para outras fazendas, o Rizoma precisa de uma instância GZappy própria.
        </p>
      </div>

      <div className="rounded-lg border bg-card px-4">
        <LinhaRegra
          titulo="Envio de avisos"
          testId="regra-envio"
          descricao={r.envioLigado
            ? "Desligar para tudo na hora, sem deploy. Os eventos continuam registrados."
            : `Desligado: nada sai. ${r.naFila} ${r.naFila === 1 ? "aviso espera" : "avisos esperam"} na fila e ${r.naFila === 1 ? "sai" : "saem"} ao religar — o que passar de 24 h expira.`}
        >
          <span className="text-sm text-muted-foreground">{r.envioLigado ? "Ligado" : "Desligado"}</span>
          <Switch
            checked={r.envioLigado}
            onCheckedChange={v => gravar({ envioLigado: v }, v ? "Envio ligado" : "Envio desligado")}
            disabled={salvar.isPending}
            aria-label="Envio de avisos"
            data-testid="switch-envio"
          />
        </LinhaRegra>
        <LinhaRegra
          titulo="Horário silencioso"
          descricao={`Os envios acontecem entre ${FAIXA_ENVIO_AVISO.inicio} e ${FAIXA_ENVIO_AVISO.fim}. O silêncio só estreita essa faixa: começa entre ${LIMITES_SILENCIO_AVISO.inicio.min} e ${LIMITES_SILENCIO_AVISO.inicio.max} e termina entre ${LIMITES_SILENCIO_AVISO.fim.min} e ${LIMITES_SILENCIO_AVISO.fim.max}. Avisos de evento nesse intervalo esperam e saem no fim dele; lembretes e resumos têm hora própria.`}
          testId="regra-silencio"
        >
          <Input
            type="time" className="w-[110px]" value={inicio ?? r.silencioInicio}
            min={LIMITES_SILENCIO_AVISO.inicio.min} max={LIMITES_SILENCIO_AVISO.inicio.max}
            onChange={e => setInicio(e.target.value)}
            onBlur={() => salvarSilencio("inicio", inicio, r.silencioInicio, () => setInicio(null))}
            data-testid="input-silencio-inicio"
          />
          <span className="text-sm text-muted-foreground">até</span>
          <Input
            type="time" className="w-[110px]" value={fim ?? r.silencioFim}
            min={LIMITES_SILENCIO_AVISO.fim.min} max={LIMITES_SILENCIO_AVISO.fim.max}
            onChange={e => setFim(e.target.value)}
            onBlur={() => salvarSilencio("fim", fim, r.silencioFim, () => setFim(null))}
            data-testid="input-silencio-fim"
          />
        </LinhaRegra>
        <LinhaRegra titulo="Janela de agrupamento" descricao="Avisos do mesmo modelo, para a mesma pessoa e com a mesma contraparte, dentro da janela viram uma mensagem." testId="regra-janela">
          <Select value={String(r.janelaMin)} onValueChange={v => gravar({ janelaMin: Number(v) }, "Janela salva")}>
            <SelectTrigger className="w-[160px]" data-testid="select-janela"><SelectValue /></SelectTrigger>
            <SelectContent>{JANELAS_AVISO_MIN.map(j => <SelectItem key={j} value={String(j)}>{j} minutos</SelectItem>)}</SelectContent>
          </Select>
        </LinhaRegra>
        <LinhaRegra titulo="Limite por pessoa por dia" descricao="Passou do limite, o resto fica para o próximo resumo. Protege o número de bloqueio." testId="regra-limite">
          <Select value={String(r.limiteDia)} onValueChange={v => gravar({ limiteDia: Number(v) }, "Limite salvo")}>
            <SelectTrigger className="w-[160px]" data-testid="select-limite"><SelectValue /></SelectTrigger>
            <SelectContent>{LIMITES_AVISO_DIA.map(l => <SelectItem key={l} value={String(l)}>{l} mensagens</SelectItem>)}</SelectContent>
          </Select>
        </LinhaRegra>
        <LinhaRegra titulo="Canal" descricao="GZappy (número do AgroPlat). Fora de produção, tudo cai na bandeja de teste (/dev/agente/inbox), como o boletim." testId="regra-canal">
          <ChipCanal canal={r.canal} />
        </LinhaRegra>
        <LinhaRegra titulo="Fuso" descricao="Horários de lembrete, resumo e silêncio." testId="regra-fuso">
          <span className="text-sm font-mono">{r.timezone}</span>
        </LinhaRegra>
      </div>
    </div>
  );
}
